import React, { useState, useCallback, useRef } from 'react';
import { prop } from 'ramda';
import { useDeviceSelector } from 'js/store/hooks';

import * as Types from './types';
import * as helpers from './helpers';

export const withSwipe = ({
    enableAxisX = true,
    enableAxisY = false,
    swipeAngle = 30 // max angle in degrees to consider move as swipe
}: Types.ISwiperParams = {}) =>
    <P extends object>(WrappedComponent: React.ComponentType<P>) =>
        (props: P): React.ReactElement<React.PropsWithChildren<P & Types.ISwiperProps>> => {
            const isTouchDevice = useDeviceSelector(prop('isTouchDevice'));
            const swipeStartedRef = useRef<boolean>(false);
            const startXRef = useRef<number>(0);
            const startYRef = useRef<number>(0);
            const [deltaX, setDeltaX] = useState(0);
            const [deltaY, setDeltaY] = useState(0);

            const swipeMove = useCallback((event: Event): void => {
                if (!swipeStartedRef.current || !enableAxisX && !enableAxisY) {
                    return;
                }

                const { posX, posY } = helpers.getTouchPosition(event);
                const currentDeltaX = startXRef.current - posX;
                const currentDeltaY = startYRef.current - posY;
                const angleX = helpers.getAngleDegrees(currentDeltaX, currentDeltaY);
                const angleY = helpers.getAngleDegrees(currentDeltaY, currentDeltaX);

                if (enableAxisX && angleX <= swipeAngle) {
                    setDeltaX(currentDeltaX);
                }

                if (enableAxisY && angleY <= swipeAngle) {
                    setDeltaX(currentDeltaY);
                }
            }, []);

            const swipeEnd = useCallback((): void => {
                if (!swipeStartedRef.current) {
                    return;
                }

                swipeStartedRef.current = false;
                startXRef.current = 0;
                startYRef.current = 0;

                setDeltaX(0);
                setDeltaY(0);
            }, []);

            const preventClick = useCallback((event: Event): void => {
                if (deltaX || deltaY) {
                    event.stopPropagation();
                }

                helpers.handleSwipeListeners({ action: 'remove', swipeEnd, swipeMove, preventClick, isTouchDevice });
            }, [deltaX, deltaY, swipeMove, swipeEnd, isTouchDevice]);

            const swipeStart = useCallback((event: React.SyntheticEvent): void => {
                if (!enableAxisX && !enableAxisY) {
                    return;
                }

                helpers.handleSwipeListeners({ action: 'add', swipeEnd, swipeMove, preventClick, isTouchDevice });

                const { posX, posY } = helpers.getTouchPosition(event.nativeEvent);

                swipeStartedRef.current = true;
                startXRef.current = posX;
                startYRef.current = posY;

                setDeltaX(0);
                setDeltaY(0);
            }, [swipeEnd, swipeMove, preventClick, isTouchDevice]);

            return (
                <WrappedComponent
                    {...props as React.PropsWithChildren<P & Types.ISwiperProps>}
                    swipeDeltaX={deltaX}
                    swipeDeltaY={deltaY}
                    swipeStart={swipeStart}
                />
            );
        };
